import { useEffect } from 'react';
import { SignIn } from '@clerk/clerk-react';
import { useAuth } from '@clerk/clerk-react';
import { useHistory, useLocation } from 'react-router-dom';
import '../css/clerk-auth.css';
import { stripeReviewReturnPath } from '../utils/stripeReviewLink';
import {
  AuthMarketingLeftColumn,
  AuthMarketingLoadingState,
} from '../components/AuthMarketingShell';

// Must match the basename logic in index.jsx and App.js.
// Without routerPush, Clerk does a full-page window.location redirect,
// so the URL must be absolute-path (e.g. /dashboard/) not router-relative (/).
const ROUTER_BASENAME = window.location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';

/**
 * SignInContent Component
 *
 * Internal component that uses Clerk hooks.
 * This component assumes ClerkProvider is present in the tree.
 *
 * IMPORTANT: ClerkProvider must NOT have routerPush/routerReplace callbacks.
 * When those are present, Clerk pushes sub-paths like /signin/factor-two through
 * react-router, causing re-renders that break the sign-in flow.
 * Without them, routing="virtual" keeps multi-step flows (factor-one, factor-two)
 * entirely in-memory — no URL changes, no re-renders.
 */
function SignInContent({ hasValidKey }) {
  const history = useHistory();
  const location = useLocation();
  const returnPath = stripeReviewReturnPath(location);
  const returnUrl = ROUTER_BASENAME.replace(/\/$/, '') + returnPath;

  const auth = useAuth();
  const { isSignedIn, isLoaded } = auth;

  const existingAccountEmail =
    typeof location.state?.existingAccountEmail === 'string'
      ? location.state.existingAccountEmail
      : '';

  // Redirect authenticated users to dashboard home
  useEffect(() => {
    if (hasValidKey && isLoaded && isSignedIn) {
      history.replace(returnPath);
    }
  }, [hasValidKey, isLoaded, isSignedIn, history, returnPath]);

  // If Clerk is not configured, show UI structure with development notice
  if (!hasValidKey) {
    return (
      <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="rounded-lg border-2 border-dashed p-6 text-center" style={{ borderColor: '#E0D9CC', backgroundColor: '#FCFBF8' }}>
            <div className="mb-2 text-sm font-semibold" style={{ color: '#3A3429' }}>
              🔧 Development Mode
            </div>
            <p className="text-xs" style={{ color: '#6B6458' }}>
              Clerk authentication is not configured. To enable authentication, add your Clerk publishable key to <code className="px-1 rounded" style={{ backgroundColor: '#F0ECE4' }}>VITE_CLERK_PUBLISHABLE_KEY</code> in <code className="px-1 rounded" style={{ backgroundColor: '#F0ECE4' }}>.env-cmdrc.json</code>.
            </p>
            <p className="mt-2 text-xs" style={{ color: '#9A9285' }}>
              The UI structure is visible for frontend development. Authentication will work once the key is configured.
            </p>
          </div>
        </div>
      </AuthMarketingLeftColumn>
    );
  }

  // Show loading state while Clerk initializes
  if (!isLoaded) {
    return (
      <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center">
        <AuthMarketingLoadingState />
      </AuthMarketingLeftColumn>
    );
  }

  // If user is already signed in, show loading while redirect happens
  if (isSignedIn) {
    return (
      <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center">
        <AuthMarketingLoadingState
          title="You&rsquo;re already signed in!"
          description="Redirecting to dashboard..."
        />
      </AuthMarketingLeftColumn>
    );
  }

  return (
    <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {existingAccountEmail ? (
          <p className="rounded-md border border-status-amber-text/30 bg-status-amber-bg px-3 py-2 text-sm text-status-amber-text">
            An account already exists for this email. Please sign in to continue.
          </p>
        ) : null}
        <div className="flex justify-center">
          <SignIn
            routing="virtual"
            signUpUrl={ROUTER_BASENAME + 'signup'}
            fallbackRedirectUrl={returnUrl}
            forceRedirectUrl={returnUrl}
            initialValues={existingAccountEmail ? { identifier: existingAccountEmail } : undefined}
            appearance={{
              variables: {
                colorPrimary: '#6B8E6B', // Warm sage green
                colorText: '#3A3429', // Warm dark brown (paper ink)
                colorTextSecondary: '#6B6458', // Medium warm gray
                colorBackground: '#F7F5F0', // Warm off-white (paper)
                colorInputBackground: '#FCFBF8', // Slightly lighter paper
                colorInputText: '#3A3429', // Warm dark brown
                borderRadius: '0.375rem', // rounded-md (6px)
              },
              elements: {
                rootBox: {
                  width: '100%',
                  maxWidth: '100%',
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderWidth: 0,
                },
                card: {
                  boxShadow: 'none', // No shadow
                  backgroundColor: '#FCFBF8', // Light paper
                },
                headerTitle: {
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#3A3429', // Warm dark brown
                  textAlign: 'center',
                  marginBottom: '0.125rem', // Minimal spacing
                },
                headerSubtitle: {
                  fontSize: '14px',
                  color: '#6B6458', // Medium warm gray
                  textAlign: 'center',
                  marginBottom: '0.5rem', // Further reduced
                },
                socialButtonsBlockButton: {
                  backgroundColor: 'transparent', // Secondary button style
                  color: '#3A3429', // Warm dark brown (secondary text)
                  border: '1px solid #E0D9CC', // Warm light gray border
                  height: '40px', // Match email input height exactly
                  minHeight: '40px', // Ensure consistent height
                  borderRadius: '6px',
                  fontSize: '14px', // text-sm
                  fontWeight: '500',
                  width: '100%',
                  padding: '0', // Remove default padding to match input
                  boxSizing: 'border-box', // Include border in height calculation
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#F0ECE4', // Warm light beige on hover
                    borderColor: '#E8E4DC', // Slightly darker border
                  },
                },
                formButtonPrimary: {
                  backgroundColor: '#6B8E6B', // Warm sage green
                  color: '#FFFFFF',
                  height: '40px', // Match email input height exactly
                  minHeight: '40px', // Ensure consistent height
                  borderRadius: '6px',
                  fontSize: '14px', // text-sm
                  fontWeight: '500',
                  width: '100%',
                  marginTop: '0.125rem', // Half of previous spacing
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0', // Remove default padding to match input
                  border: 'none', // Remove default border
                  boxSizing: 'border-box', // Include border in height calculation
                  '&:hover': {
                    backgroundColor: '#5B785B', // Darker sage green
                  },
                },
                footerAction: {
                  display: 'flex',
                  alignItems: 'baseline', // Use baseline instead of center
                  justifyContent: 'center',
                  gap: '0.25rem',
                  fontSize: '14px',
                  lineHeight: '1.5',
                },
                footerActionLink: {
                  color: '#6B8E6B', // Warm sage green
                  fontSize: '14px',
                  lineHeight: '1.5',
                  verticalAlign: 'baseline',
                  display: 'inline',
                  margin: 0,
                  padding: 0,
                  '&:hover': {
                    color: '#5B785B', // Darker sage green
                  },
                },
                identityPreviewEditButton: {
                  color: '#6B8E6B', // Warm sage green
                  '&:hover': {
                    color: '#5B785B', // Darker sage green
                  },
                },
                formFieldInput: {
                  height: '40px', // h-10 (matching dashboard input)
                  minHeight: '40px', // Ensure consistent height
                  borderColor: '#E0D9CC', // Warm light gray (paper border)
                  borderRadius: '6px', // rounded-md
                  padding: '8px 12px', // py-2 px-3 (8px vertical, 12px horizontal)
                  fontSize: '14px', // text-sm
                  backgroundColor: '#FCFBF8', // Light paper
                  color: '#3A3429', // Warm dark brown
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  width: '100%',
                  boxSizing: 'border-box', // Include border in height calculation
                  '&::placeholder': {
                    color: '#9A9285', // Warm muted gray
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                },
                input: {
                  height: '40px', // h-10
                  borderColor: '#E0D9CC', // Warm light gray
                  borderRadius: '6px', // rounded-md
                  padding: '8px 12px', // py-2 px-3
                  fontSize: '14px', // text-sm
                  backgroundColor: '#FCFBF8', // Light paper
                  color: '#3A3429', // Warm dark brown
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  width: '100%',
                  '&::placeholder': {
                    color: '#9A9285', // Warm muted gray
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                },
                formFieldInput__phone: {
                  height: '40px', // h-10 (matching email input)
                  borderColor: '#E0D9CC', // Warm light gray
                  borderRadius: '6px', // rounded-md
                  padding: '8px 12px', // py-2 px-3
                  fontSize: '14px', // text-sm
                  backgroundColor: '#FCFBF8', // Light paper
                  color: '#3A3429', // Warm dark brown
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  width: '100%',
                  '&::placeholder': {
                    color: '#9A9285', // Warm muted gray
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                },
                formFieldInput__identifier: {
                  height: '40px', // h-10 (matching phone input)
                  borderColor: '#E0D9CC', // Warm light gray
                  borderRadius: '6px', // rounded-md
                  padding: '8px 12px', // py-2 px-3
                  fontSize: '14px', // text-sm
                  backgroundColor: '#FCFBF8', // Light paper
                  color: '#3A3429', // Warm dark brown
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  width: '100%',
                  '&::placeholder': {
                    color: '#9A9285', // Warm muted gray
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: '#6B8E6B', // Warm sage green
                    boxShadow: '0 0 0 2px rgba(107, 142, 107, 0.15)', // Warm sage green ring
                  },
                },
                formFieldLabel: {
                  color: '#3A3429', // Warm dark brown
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '0', // No spacing between label and input
                },
                dividerLine: {
                  backgroundColor: '#E8E4DC', // Warm light gray
                },
                dividerText: {
                  color: '#6B6458', // Medium warm gray
                  fontSize: '14px',
                },
                divider: {
                  marginTop: '0.5rem', // Minimal spacing above divider
                  marginBottom: '0.5rem', // Minimal spacing below divider
                },
                socialButtons: {
                  marginBottom: '0.5rem', // Minimal spacing below social buttons
                },
                main: {
                  paddingTop: '0', // Remove extra padding
                  paddingBottom: '0',
                },
              },
            }}
          />
        </div>
      </div>
    </AuthMarketingLeftColumn>
  );
}

export default SignInContent;
